/**
 * Sentry Configuration for SlabReality Frontend
 * ==============================================
 * ENTERPRISE-GRADE PRODUCTION MONITORING
 * 
 * Features:
 * - Session Replay (on error)
 * - Performance Tracing
 * - Web Vitals
 * - User Context (id, role, browser, device)
 * - Route Transition Tracking
 * - Critical Workflow Tracking
 * 
 * CRITICAL WORKFLOWS TRACKED:
 * - Auth (login/logout/register)
 * - Visual Search
 * - Proposal Workflow
 * - Render Generation
 * - Client Approval
 */

import * as Sentry from '@sentry/react';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Sentry configuration from environment
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const SENTRY_ENVIRONMENT = process.env.REACT_APP_SENTRY_ENVIRONMENT || (isProduction ? 'production' : 'development');
const SENTRY_RELEASE = process.env.REACT_APP_VERSION || 'slabreality-v1';

// Track if Sentry is initialized
let sentryInitialized = false;

/**
 * Get browser context
 */
function getBrowserContext() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';
  
  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    version = ua.split('Firefox/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    version = ua.split('Edg/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    version = ua.split('Chrome/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.split('Version/')[1]?.split(' ')[0] || '';
  }
  
  return {
    name: browser,
    version: version,
    user_agent: ua,
  };
}

/**
 * Get device context
 */
function getDeviceContext() {
  const ua = navigator.userAgent;
  let device = 'Desktop';
  let os = 'Unknown';
  
  if (/iPhone|iPad|iPod/.test(ua)) {
    device = /iPad/.test(ua) ? 'Tablet' : 'Mobile';
    os = 'iOS';
  } else if (/Android/.test(ua)) {
    device = /Mobile/.test(ua) ? 'Mobile' : 'Tablet';
    os = 'Android';
  } else if (/Windows/.test(ua)) {
    os = 'Windows';
  } else if (/Mac/.test(ua)) {
    os = 'macOS';
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  }
  
  return {
    type: device,
    os: os,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    pixel_ratio: window.devicePixelRatio,
    online: navigator.onLine,
  };
}

/**
 * Get connection context (Network Information API)
 */
function getConnectionContext() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return null;
  
  return {
    type: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

/**
 * Initialize Sentry
 * Call this at app startup (before ReactDOM.render)
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.log('[Sentry] DSN not configured - monitoring disabled');
    return;
  }
  
  if (sentryInitialized) {
    console.log('[Sentry] Already initialized');
    return;
  }
  
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    
    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track all page navigations
        enableInp: true,
      }),
      // Session Replay - captures user sessions on errors
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: false,
        // Block all media for performance
        blockAllMedia: false,
        // Network request bodies
        networkDetailAllowUrls: ['/api/'],
      }),
    ],
    
    // Performance monitoring - sample rates
    tracesSampleRate: isProduction ? 0.3 : 1.0,  // 30% in prod
    
    // Profiling - measure JS execution
    profilesSampleRate: isProduction ? 0.1 : 0.5,
    
    // Session Replay sample rates
    replaysSessionSampleRate: isProduction ? 0.05 : 0.0,  // 5% of all sessions
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0.5,   // 100% on error
    
    // Filter out noisy errors
    ignoreErrors: [
      // Browser noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection',
      // Network (handled by specific handlers)
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      'AbortError',
      // Expected auth states
      'User not authenticated',
      'Session expired',
      'PGRST116', // Supabase "row not found"
    ],
    
    // Filter transactions
    ignoreTransactions: [
      '/health',
      '/api/health',
      'favicon.ico',
    ],
    
    // Enrichment before send
    beforeSend(event, hint) {
      // Add browser/device/connection context
      event.contexts = {
        ...event.contexts,
        browser: getBrowserContext(),
        device: getDeviceContext(),
        connection: getConnectionContext(),
      };
      
      // Add locale
      event.tags = {
        ...event.tags,
        locale: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      
      return event;
    },
    
    // Filter breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter noisy console logs
      if (breadcrumb.category === 'console') {
        const message = breadcrumb.message || '';
        if (
          message.includes('[AUTH]') ||
          message.includes('[Blueprint]') ||
          message.includes('🧠') ||
          message.includes('🔒') ||
          message.includes('Download the React DevTools')
        ) {
          return null;
        }
      }
      return breadcrumb;
    },
    
    // Transaction context
    beforeSendTransaction(event) {
      // Add performance context
      if (event.contexts?.trace) {
        event.tags = {
          ...event.tags,
          'trace.op': event.contexts.trace.op,
        };
      }
      return event;
    },
  });
  
  sentryInitialized = true;
  console.log('[Sentry] Initialized for', SENTRY_ENVIRONMENT);
}

/**
 * Check if Sentry is initialized
 */
export function isSentryInitialized() {
  return sentryInitialized;
}

/**
 * Set user context after login
 * @param {Object} user - User object with id, email, role
 * @param {Object} profile - Profile object with additional data
 */
export function setUserContext(user, profile = null) {
  if (!sentryInitialized) return;
  
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  
  // Set user identity
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: profile?.full_name || user.email?.split('@')[0],
  });
  
  // Set user tags for filtering
  const role = profile?.role_system || profile?.role || 'user';
  Sentry.setTag('user.role', role);
  Sentry.setTag('user.plan', profile?.plan_id || 'free');
  Sentry.setTag('user.onboarded', profile?.onboarding_completed ? 'yes' : 'no');
  
  // Set user context with full details
  Sentry.setContext('user_profile', {
    id: user.id,
    email: user.email,
    role: role,
    business_type: profile?.business_type,
    company: profile?.company_name,
    plan: profile?.plan_id,
    onboarding_completed: profile?.onboarding_completed,
    created_at: user.created_at,
  });
}

/**
 * Clear user context on logout
 */
export function clearUserContext() {
  if (!sentryInitialized) return;
  
  Sentry.setUser(null);
  Sentry.setTag('user.role', null);
  Sentry.setTag('user.plan', null);
  Sentry.setTag('user.onboarded', null);
}

/**
 * Set current page/route context
 */
export function setRouteContext(route, params = {}) {
  if (!sentryInitialized) return;
  
  Sentry.setTag('route', route);
  Sentry.setContext('route', {
    path: route,
    params: params,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Set proposal context for proposal operations
 */
export function setProposalContext(proposalId, proposalTitle, clientEmail = null) {
  if (!sentryInitialized) return;
  
  Sentry.setContext('proposal', {
    id: proposalId,
    title: proposalTitle,
    client_email: clientEmail,
  });
  Sentry.setTag('proposal.id', proposalId);
}

/**
 * Clear proposal context
 */
export function clearProposalContext() {
  if (!sentryInitialized) return;
  
  Sentry.setContext('proposal', null);
  Sentry.setTag('proposal.id', null);
}

/**
 * Set render context for SRE operations
 */
export function setRenderContext(renderId, materialName, renderType = 'sre') {
  if (!sentryInitialized) return;
  
  Sentry.setContext('render', {
    id: renderId,
    material: materialName,
    type: renderType,
  });
  Sentry.setTag('render.id', renderId);
  Sentry.setTag('render.type', renderType);
}

/**
 * Set visual search context
 */
export function setVisualSearchContext(searchId, imageSize = null) {
  if (!sentryInitialized) return;
  
  Sentry.setContext('visual_search', {
    search_id: searchId,
    image_size: imageSize,
    timestamp: new Date().toISOString(),
  });
  Sentry.setTag('visual_search.id', searchId);
}

// ============================================================
// PERFORMANCE TRACING
// ============================================================

/**
 * Start a performance transaction
 * @returns {Object} Transaction object with finish() method
 */
export function startTransaction(name, op = 'task') {
  if (!sentryInitialized) {
    return { finish: () => {}, setTag: () => {}, setData: () => {} };
  }
  
  return Sentry.startInactiveSpan({
    name,
    op,
    forceTransaction: true,
  });
}

/**
 * Create a child span within current transaction
 */
export function startSpan(name, op = 'task', callback) {
  if (!sentryInitialized) {
    return callback ? callback() : null;
  }
  
  return Sentry.startSpan({ name, op }, callback);
}

// ============================================================
// ERROR CAPTURE
// ============================================================

/**
 * Capture an exception with context
 */
export function captureException(error, context = {}) {
  if (!sentryInitialized) {
    console.error('[Sentry disabled] Exception:', error);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
    tags: context.tags || {},
  });
}

/**
 * Capture a message with level
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!sentryInitialized) {
    console.log('[Sentry disabled] Message:', message);
    return;
  }
  
  Sentry.captureMessage(message, {
    level,
    extra: context,
    tags: context.tags || {},
  });
}

/**
 * Add a breadcrumb
 */
export function addBreadcrumb(category, message, data = {}, level = 'info') {
  if (!sentryInitialized) return;
  
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  });
}

// ============================================================
// WORKFLOW TRACKERS
// ============================================================

export const WorkflowTracker = {
  // ==================== AUTH WORKFLOWS ====================
  authStart(method = 'email') {
    addBreadcrumb('auth', `Auth started via ${method}`, { method });
    return startTransaction(`auth.${method}`, 'auth');
  },
  
  authSuccess(userId, method = 'email') {
    addBreadcrumb('auth', 'Auth successful', { userId, method });
    captureMessage(`User authenticated via ${method}`, 'info', {
      tags: { workflow: 'auth', method },
    });
  },
  
  authFailed(error, method = 'email') {
    captureMessage('Authentication failed', 'warning', {
      tags: { workflow: 'auth', method, error_type: error?.name || 'unknown' },
      error: error?.message,
    });
  },
  
  logout() {
    addBreadcrumb('auth', 'User logged out');
    clearUserContext();
  },
  
  // ==================== VISUAL SEARCH WORKFLOWS ====================
  visualSearchStart(imageSize = null) {
    const searchId = `vs_${Date.now()}`;
    setVisualSearchContext(searchId, imageSize);
    addBreadcrumb('visual_search', 'Visual search started', { searchId, imageSize });
    return {
      searchId,
      transaction: startTransaction('visual_search', 'search'),
    };
  },
  
  visualSearchDNAExtracted(searchId, dnaData) {
    addBreadcrumb('visual_search', 'DNA extracted', { 
      searchId, 
      color: dnaData?.dominant_color,
      pattern: dnaData?.pattern_type,
    });
  },
  
  visualSearchRoboflowComplete(searchId, prediction, confidence) {
    addBreadcrumb('visual_search', 'Roboflow classification complete', {
      searchId,
      prediction,
      confidence,
    });
  },
  
  visualSearchComplete(searchId, resultCount, topMatch) {
    addBreadcrumb('visual_search', 'Visual search complete', {
      searchId,
      resultCount,
      topMatch: topMatch?.name,
      topScore: topMatch?.score,
    });
  },
  
  visualSearchFailed(searchId, error) {
    captureException(error, {
      tags: { workflow: 'visual_search', searchId },
      searchId,
    });
  },
  
  // ==================== PROPOSAL WORKFLOWS ====================
  proposalCreate(title) {
    addBreadcrumb('proposal', 'Creating proposal', { title });
    return startTransaction('proposal.create', 'proposal');
  },
  
  proposalSend(proposalId, clientEmail) {
    setProposalContext(proposalId, null, clientEmail);
    addBreadcrumb('proposal', 'Sending proposal', { proposalId, clientEmail });
    captureMessage('Proposal sent to client', 'info', {
      tags: { workflow: 'proposal', action: 'send' },
    });
  },
  
  proposalViewed(proposalId, byClient = true) {
    addBreadcrumb('proposal', 'Proposal viewed', { proposalId, byClient });
  },
  
  proposalApproved(proposalId, materialId) {
    addBreadcrumb('proposal', 'Proposal approved', { proposalId, materialId });
    captureMessage('Proposal approved by client', 'info', {
      tags: { workflow: 'proposal', action: 'approve' },
    });
  },
  
  proposalRejected(proposalId, reason) {
    addBreadcrumb('proposal', 'Proposal rejected', { proposalId, reason });
    captureMessage('Proposal rejected by client', 'info', {
      tags: { workflow: 'proposal', action: 'reject' },
    });
  },
  
  proposalFailed(proposalId, error) {
    captureException(error, {
      tags: { workflow: 'proposal', proposalId },
    });
  },
  
  // ==================== RENDER WORKFLOWS ====================
  renderStart(materialName, surfaceType) {
    const renderId = `render_${Date.now()}`;
    setRenderContext(renderId, materialName, 'sre');
    addBreadcrumb('render', 'Render started', { renderId, materialName, surfaceType });
    return {
      renderId,
      transaction: startTransaction('render.generate', 'render'),
    };
  },
  
  renderProgress(renderId, stage, progress) {
    addBreadcrumb('render', `Render progress: ${stage}`, { renderId, stage, progress });
  },
  
  renderComplete(renderId, realismScore) {
    addBreadcrumb('render', 'Render complete', { renderId, realismScore });
    captureMessage('Render completed successfully', 'info', {
      tags: { workflow: 'render', renderId },
      realismScore,
    });
  },
  
  renderFailed(renderId, error) {
    captureException(error, {
      tags: { workflow: 'render', renderId },
    });
  },
  
  // ==================== ROUTE TRANSITIONS ====================
  routeChange(from, to) {
    setRouteContext(to);
    addBreadcrumb('navigation', `Route: ${from} → ${to}`, { from, to });
  },
};

// ============================================================
// ERROR BOUNDARY COMPONENT
// ============================================================

export const SentryErrorBoundary = Sentry.ErrorBoundary;

// ============================================================
// TEST FUNCTIONS (for validation)
// ============================================================

/**
 * Trigger a test error to verify Sentry is capturing
 */
export function triggerTestError() {
  if (!sentryInitialized) {
    console.warn('[Sentry] Not initialized - cannot trigger test error');
    return { success: false, reason: 'not_initialized' };
  }
  
  try {
    // Capture a test message first
    const messageId = Sentry.captureMessage('SlabReality Frontend Test Event', {
      level: 'info',
      tags: { 
        test: 'true',
        source: 'manual_test',
      },
      extra: {
        timestamp: new Date().toISOString(),
        browser: getBrowserContext(),
        device: getDeviceContext(),
      },
    });
    
    console.log('[Sentry] Test message sent, ID:', messageId);
    
    return { 
      success: true, 
      messageId,
      dsn_configured: true,
      environment: SENTRY_ENVIRONMENT,
    };
  } catch (error) {
    console.error('[Sentry] Test failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Trigger a test exception
 */
export function triggerTestException() {
  if (!sentryInitialized) {
    console.warn('[Sentry] Not initialized - cannot trigger test exception');
    return { success: false, reason: 'not_initialized' };
  }
  
  try {
    const testError = new Error('SlabReality Frontend Test Exception - This is intentional');
    testError.name = 'SentryTestException';
    
    const eventId = Sentry.captureException(testError, {
      tags: {
        test: 'true',
        source: 'manual_test',
      },
      extra: {
        timestamp: new Date().toISOString(),
        intentional: true,
      },
    });
    
    console.log('[Sentry] Test exception sent, Event ID:', eventId);
    
    return {
      success: true,
      eventId,
      dsn_configured: true,
      environment: SENTRY_ENVIRONMENT,
    };
  } catch (error) {
    console.error('[Sentry] Test exception failed:', error);
    return { success: false, error: error.message };
  }
}

// Export Sentry for direct access if needed
export { Sentry };
